<script lang="ts">
  /**
   * Import a statement — the fourth section of the Expenses screen.
   *
   * The file is read HERE, in the browser, and never uploaded (spec ruling 2).
   * What goes up is transaction lines the owner has looked at and accepted;
   * nothing is written until he presses the button (ruling 3).
   *
   * The interaction is StableBooks': gate in stages rather than dropping a
   * hundred rows on someone at once. First the column mapping, but only when
   * the parser is not sure of it. Then the rows it could not read. Then the
   * table — where credits and suspected duplicates arrive UN-TICKED, which is
   * the single most valuable default in the whole feature: the owner adds
   * spending on purpose and never by not noticing.
   *
   * CSV only for now. `readStatement` is a switch on the file's kind with one
   * arm filled in; PDF extraction becomes a second arm producing the same
   * StatementLine[], and nothing below it changes.
   */
  import { api, isApiError } from '../../lib/api';
  import { backOutGst } from '../../lib/calc/expenses';
  import { priceInForce } from '../../lib/calc/pnl';
  import { money } from '../../lib/money';
  import type { ReconcileResult } from '../../lib/statements/reconcile';
  import { detectColumns, type ColumnGuess, type ColumnMapping } from '../../lib/statements/columns';
  import { parseCsvRows } from '../../lib/statements/csv';
  import type { DateOrder } from '../../lib/statements/dates';
  import { fileFingerprint } from '../../lib/statements/fingerprint';
  import { buildLines, toImportLine, type BuiltStatement, type StatementLine } from '../../lib/statements/lines';
  import { notify, notifyError } from '../../lib/store';
  import type {
    DuplicateExpense,
    DuplicateIncome,
    Expense,
    ExpenseCategory,
    ImportStatementLine,
    Income,
    SiteSummary,
    Statement,
    StatementKind,
    StatementLineCheck,
    StatementSummary,
    TaxRate
  } from '../../lib/types';

  interface Props {
    categories: readonly ExpenseCategory[];
    taxRates: readonly TaxRate[];
    /** Called once an import lands, so the month behind this reloads. */
    onimported: () => void;
  }

  const { categories, taxRates, onimported }: Props = $props();

  /** `.pdf` joins this the day extraction exists; nothing else has to move. */
  const ACCEPT = '.csv,text/csv';

  type Step = 'choose' | 'mapping' | 'skipped' | 'review';
  let step = $state<Step>('choose');

  let label = $state('');
  let kind = $state<StatementKind>('csv');
  let fingerprint = $state('');
  let parseError = $state<string | null>(null);
  let guess = $state<ColumnGuess | null>(null);
  let mapping = $state<ColumnMapping | null>(null);
  let built = $state<BuiltStatement | null>(null);
  let alreadyImported = $state<Statement | null>(null);
  let checking = $state(false);
  let committing = $state(false);

  /** As printed on the statement, typed in fresh for each import — never carried over. */
  let openingBalance = $state('');
  let closingBalance = $state('');
  /** opening_balance / closing_balance, set only when commit() finds a
   * balance that was typed in but still cannot be read as a number. */
  let fieldErrors = $state<Record<string, string>>({});

  /**
   * What the last completed import found. Deliberately NOT cleared by
   * `reset()` — that runs right after a successful commit, and the owner
   * needs to see this result on the screen it lands him back on. It is only
   * cleared when a new file is chosen, which is the point at which it stops
   * being about anything on screen. Ordinary instance state is enough for
   * this now that `AdminExpenses.svelte`'s reload no longer unmounts this
   * component on every refresh (see `loading`/`refreshing` there).
   */
  let importBanner = $state<{ label: string; result: ReconcileResult } | null>(null);

  /** One row of the review table: what was read, plus what the owner did to it. */
  interface ReviewRow {
    line: StatementLine;
    include: boolean;
    category: string;
    description: string;
    hadGst: boolean;
    /** Which project this line belongs to. '' is company-wide. */
    jobSite: string;
    check: StatementLineCheck | null;
  }

  let rows = $state<ReviewRow[]>([]);

  const liveCategories = $derived(categories.filter((c) => c.archived === 0));

  let jobSites = $state<SiteSummary[]>([]);
  /** A finished site drops off the picker; nothing already tagged to one changes. */
  const activeSites = $derived(jobSites.filter((s) => s.status === 'active'));
  /** The "set every line to…" control's own choice, applied on demand rather than bound. */
  let bulkJobSite = $state('');

  // Built as one string rather than markup: a leading space inside an {#if}
  // is collapsed away, which ran "CSV" straight into the separator.
  const fileSummary = $derived(
    built ? `${kind.toUpperCase()} · ${built.lines.length} lines read` : kind.toUpperCase()
  );

  /** Where a matching expense came from, so the owner can place it. */
  function whereFrom(dup: DuplicateExpense): string {
    if (dup.statement_label) return `(from ${dup.statement_label}).`;
    if (dup.source === 'manual') return '(typed in).';
    // Imported, but the record of that import has since been removed.
    return '(from an earlier import).';
  }

  /**
   * The deposit-side counterpart to `whereFrom`. `Income` carries no `source`
   * field the way `Expense` does — a null `statement_id` is itself "typed in
   * by hand" — so the same three cases are told apart differently here.
   */
  function whereFromIncome(dup: DuplicateIncome): string {
    if (dup.statement_label) return `(from ${dup.statement_label}).`;
    if (dup.statement_id === null) return '(typed in).';
    return '(from an earlier import).';
  }

  /**
   * '' means "not on the statement" — never zero. Strips a leading `$` and
   * any thousands separators first, so "$1,250.00" — a completely plausible
   * way to copy a balance off a statement — parses the same as "1250.00".
   * Still returns null for text that cannot be read at all; `commit()`
   * checks for that case itself with `isUnreadableBalance` so a typo turns
   * into a field error instead of silently being treated the same as a
   * balance the owner chose to leave blank.
   */
  function parseOptionalMoney(text: string): number | null {
    const trimmed = text.trim();
    if (trimmed === '') return null;
    const cleaned = trimmed.replace(/^\$/, '').replace(/,/g, '');
    // `Number('')` is 0, not NaN — so a box holding only punctuation ("$", ",")
    // would otherwise come back as a real, committed balance of $0.00 and feed
    // that fabricated figure straight into the reconciliation. Nothing left
    // after the cleanup means nothing was readable.
    if (cleaned === '') return null;
    const value = Number(cleaned);
    return Number.isFinite(value) ? value : null;
  }

  /** True when the owner typed something into a balance box that still does
   * not parse — as opposed to leaving it blank, which is the deliberate way
   * to skip the reconciliation check. */
  function isUnreadableBalance(text: string): boolean {
    return text.trim() !== '' && parseOptionalMoney(text) === null;
  }

  /**
   * `not_checked` must never read as a pass, and `out` must never read as a
   * failure — the import already happened by the time this shows. `'info'`
   * and `'warn'` (never `'error'`) keep both of those true against the
   * shared `.banner` styles.
   */
  function reconciliationBanner(result: ReconcileResult): { tone: 'ok' | 'warn' | 'info'; text: string } {
    if (result.status === 'not_checked') {
      return { tone: 'info', text: 'No balances entered, so nothing was checked.' };
    }
    if (result.status === 'out') {
      return {
        tone: 'warn',
        text: `Out by ${money(Math.abs(result.difference ?? 0))}. This only counts what was actually imported — if a duplicate was skipped or a line was unticked above, being out is expected. If not, a line may be missing or in twice. The import went through — check the lines above.`
      };
    }
    return { tone: 'ok', text: 'This statement balances.' };
  }

  /** "1 expense" / "2 deposits". */
  function countPhrase(n: number, noun: string): string {
    return `${n} ${noun}${n === 1 ? '' : 's'}`;
  }

  /**
   * What an import still holds, in words that name both sides. `line_count`
   * counts both directions but `expense_count` alone only ever counted the
   * debit side — used on its own it made a perfectly good import that also
   * held a deposit read as "1 of 2 still saved", which looks like a failure
   * when nothing failed.
   */
  function savedParts(statement: StatementSummary): string[] {
    const parts: string[] = [];
    if (statement.expense_count > 0) parts.push(countPhrase(statement.expense_count, 'expense'));
    if (statement.income_count > 0) parts.push(countPhrase(statement.income_count, 'deposit'));
    return parts;
  }

  /** The removal-confirmation sentence: what stays behind once the RECORD goes. */
  function keptPhrase(statement: StatementSummary): string {
    const parts = savedParts(statement);
    return parts.length === 0 ? 'Nothing from it is still saved.' : `Its ${parts.join(' and ')} stay.`;
  }

  /** The reasoning, less anything already said louder as a warning. */
  const reasons = $derived.by(() => {
    const read = guess;
    return read ? read.notes.filter((note) => !read.warnings.includes(note)) : [];
  });

  function gstOn(onDate: string): number {
    return priceInForce(taxRates.filter((r) => r.kind === 'gst'), onDate)?.percent ?? 0;
  }

  /* ---- reading the file ------------------------------------------------- */

  function kindOf(name: string): StatementKind | null {
    const lower = name.toLowerCase();
    if (lower.endsWith('.csv')) return 'csv';
    if (lower.endsWith('.pdf')) return 'pdf';
    return null;
  }

  function reset() {
    step = 'choose';
    guess = null;
    mapping = null;
    built = null;
    rows = [];
    alreadyImported = null;
    parseError = null;
    label = '';
    fingerprint = '';
    openingBalance = '';
    closingBalance = '';
    fieldErrors = {};
    bulkJobSite = '';
  }

  async function chooseFile(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    // Clear it straight away so choosing the same file twice still fires.
    input.value = '';
    if (!file) return;
    // A fresh file means whatever the last import's banner said is no longer
    // about anything on screen.
    importBanner = null;
    reset();

    const chosen = kindOf(file.name);
    if (chosen === 'pdf') {
      parseError = 'PDF statements are not read yet. Download the month as CSV and choose that.';
      return;
    }
    if (chosen !== 'csv') {
      parseError = 'That is not a CSV. Download the month from the bank as a CSV file.';
      return;
    }

    try {
      const text = await file.text();
      label = file.name;
      kind = chosen;
      fingerprint = fileFingerprint(text);
      const read = detectColumns(parseCsvRows(text));
      if (read.dataRows.length === 0) {
        parseError = 'That file has no rows under its header.';
        return;
      }
      guess = read;
      mapping = { ...read.mapping };
      // The mapping is only worth someone's time when the parser is unsure of
      // it; a clean header goes straight on to the rows.
      if (read.warnings.length > 0) step = 'mapping';
      else applyMapping();
    } catch (cause) {
      notifyError(cause, 'Could not read that file');
    }
  }

  /** Re-read every row through the mapping as it now stands. */
  function applyMapping() {
    if (!guess || !mapping) return;
    built = buildLines(guess.dataRows, mapping);
    if (built.lines.length === 0) {
      parseError = 'No row in that file could be read as a transaction. Check the columns above.';
      step = 'mapping';
      return;
    }
    parseError = null;
    if (built.skipped.length > 0) step = 'skipped';
    else void openReview();
  }

  /* ---- the review table ------------------------------------------------- */

  async function openReview() {
    if (!built) return;
    step = 'review';
    rows = built.lines.map((line) => ({
      line,
      // A credit is money in, not an expense. Off until the owner says so.
      include: line.direction === 'debit',
      category: '',
      description: '',
      hadGst: true,
      jobSite: '',
      check: null
    }));
    checking = true;
    try {
      const answer = await api.checkStatementLines({
        fingerprint,
        lines: built.lines.map((line) => ({
          incurred_on: line.incurred_on,
          merchant: line.description,
          amount_total: line.amount,
          // Absent here would default to 'debit' server-side, which would
          // check a deposit against the wrong table and never flag it.
          direction: line.direction
        }))
      });
      alreadyImported = answer.already_imported;
      rows = rows.map((row, i) => {
        const check = answer.lines[i] ?? null;
        return {
          ...row,
          check,
          // A suspected duplicate is shown, never dropped — and it starts off,
          // so importing it twice takes a deliberate tick. A debit checks
          // `duplicates`, a credit checks `income_duplicates`; only one of
          // the two is ever populated for a given line.
          include:
            row.include &&
            (check?.duplicates.length ?? 0) === 0 &&
            (check?.income_duplicates.length ?? 0) === 0
        };
      });
    } catch (cause) {
      notifyError(cause, 'Could not check these lines');
    } finally {
      checking = false;
    }
  }

  function setAll(include: boolean) {
    rows = rows.map((row) => ({ ...row, include }));
  }

  /** The "set every line to…" control: a month of fuel for one job in one click. */
  function setAllJobSites(jobSite: string) {
    rows = rows.map((row) => ({ ...row, jobSite }));
  }

  const chosen = $derived(rows.filter((row) => row.include));
  const chosenTotal = $derived(
    Math.round(chosen.reduce((sum, row) => sum + row.line.amount, 0) * 100) / 100
  );
  const chosenExGst = $derived(
    Math.round(
      chosen.reduce(
        (sum, row) =>
          sum + backOutGst(row.line.amount, row.hadGst, gstOn(row.line.incurred_on)).amount_ex_gst,
        0
      ) * 100
    ) / 100
  );
  const duplicateCount = $derived(
    rows.filter((row) => (row.check?.duplicates.length ?? 0) > 0 || (row.check?.income_duplicates.length ?? 0) > 0)
      .length
  );
  const leaseCount = $derived(rows.filter((row) => (row.check?.lease_clashes.length ?? 0) > 0).length);
  const creditCount = $derived(rows.filter((row) => row.line.direction === 'credit').length);

  async function commit() {
    if (committing || chosen.length === 0 || !built) return;
    const nextErrors: Record<string, string> = {};
    if (isUnreadableBalance(openingBalance)) nextErrors.opening_balance = 'Could not read this as a number';
    if (isUnreadableBalance(closingBalance)) nextErrors.closing_balance = 'Could not read this as a number';
    fieldErrors = nextErrors;
    if (nextErrors.opening_balance || nextErrors.closing_balance) return;
    committing = true;
    // The mapping itself — direction, the credit/no-category rule, the
    // job-site tag — lives in ./lib/statements/lines.ts so it is unit
    // tested there rather than only reachable by clicking through this
    // screen. This is deliberately the only place that calls it.
    const lines: ImportStatementLine[] = chosen.map(toImportLine);
    try {
      const result = await api.importStatement({
        label,
        kind,
        period_start: built.period_start,
        period_end: built.period_end,
        fingerprint,
        lines,
        opening_balance: parseOptionalMoney(openingBalance),
        closing_balance: parseOptionalMoney(closingBalance)
      });
      // Everything this commit actually saved, both directions — not just
      // the expense side, or a file of fuel and one deposit reports as
      // having imported fewer lines than it did.
      const savedCount = result.expenses.length + result.income.length;
      notify({
        tone: 'ok',
        title: `${savedCount} line${savedCount === 1 ? '' : 's'} imported`,
        detail: label
      });
      importBanner = { label, result: result.reconciliation };
      reset();
      await loadStatements();
      onimported();
    } catch (cause) {
      notifyError(cause, isApiError(cause) ? cause.message : 'Could not import those lines');
    } finally {
      committing = false;
    }
  }

  /* ---- what has been imported before ------------------------------------ */

  let statements = $state<StatementSummary[]>([]);
  let openStatement = $state<number | null>(null);
  /** One line of "what it brought in" — an expense or a deposit, told apart by `direction`. */
  interface BroughtLine {
    date: string;
    label: string;
    amount: number;
    direction: 'debit' | 'credit';
  }
  let openLines = $state<BroughtLine[]>([]);
  let confirmDelete = $state<number | null>(null);

  async function loadStatements() {
    try {
      statements = (await api.listStatements()).statements;
    } catch (cause) {
      notifyError(cause, 'Could not load the imports');
    }
  }

  async function loadSites() {
    try {
      jobSites = (await api.listSites()).sites;
    } catch (cause) {
      notifyError(cause, 'Could not load the job sites');
    }
  }

  $effect(() => {
    void loadStatements();
    void loadSites();
  });

  /** An expense and a deposit are shown together, oldest first — same as the statement read them. */
  function broughtLines(expenses: readonly Expense[], income: readonly Income[]): BroughtLine[] {
    const debits: BroughtLine[] = expenses.map((e) => ({
      date: e.incurred_on,
      label: e.merchant || e.description,
      amount: e.amount_total,
      direction: 'debit'
    }));
    const credits: BroughtLine[] = income.map((i) => ({
      date: i.received_on,
      label: i.source_label || i.description,
      amount: i.amount_total,
      direction: 'credit'
    }));
    return [...debits, ...credits].sort((a, b) => a.date.localeCompare(b.date));
  }

  async function toggleStatement(statementId: number) {
    if (openStatement === statementId) {
      openStatement = null;
      return;
    }
    openStatement = statementId;
    openLines = [];
    try {
      const detail = await api.getStatement(statementId);
      openLines = broughtLines(detail.expenses, detail.income);
    } catch (cause) {
      notifyError(cause, 'Could not open that import');
    }
  }

  async function removeStatement(statementId: number) {
    try {
      await api.deleteStatement(statementId);
      confirmDelete = null;
      if (openStatement === statementId) openStatement = null;
      notify({ tone: 'ok', title: 'Import record removed', detail: 'Its expenses were kept.' });
      await loadStatements();
      onimported();
    } catch (cause) {
      notifyError(cause, 'Could not remove that import');
    }
  }

  /* ---- the mapping panel's pickers -------------------------------------- */

  const columnNumbers = $derived(
    guess ? Array.from({ length: columnCount(guess) }, (_, i) => i) : []
  );

  function columnCount(g: ColumnGuess): number {
    const widest = g.dataRows.reduce((w, row) => Math.max(w, row.length), 0);
    return Math.max(widest, g.mapping.headers.length);
  }

  function columnLabel(index: number): string {
    const name = mapping?.headers[index]?.trim();
    return name ? `${index + 1} · ${name}` : `Column ${index + 1}`;
  }

  function setColumn(field: 'dateIndex' | 'descriptionIndex' | 'amountIndex' | 'debitIndex' | 'creditIndex', value: string) {
    if (!mapping) return;
    mapping = { ...mapping, [field]: Number(value) };
  }

  function setOrder(value: string) {
    if (!mapping) return;
    mapping = { ...mapping, dateOrder: value as DateOrder };
  }

  function setShape(value: string) {
    if (!mapping) return;
    const shape = value === 'debit_credit' ? 'debit_credit' : 'single';
    mapping = { ...mapping, shape };
  }

  function setSigns(value: string) {
    if (!mapping) return;
    mapping = { ...mapping, debitIsPositive: value === 'positive' };
  }
</script>

<p class="note">
  The file is read on this phone and never uploaded — only the lines you accept are saved. Nothing
  is written until you press <b>Import</b>.
</p>

{#if importBanner}
  {@const info = reconciliationBanner(importBanner.result)}
  <div class="banner banner--{info.tone}" role="status">
    <div>
      <strong>{importBanner.label} imported</strong>
      <p>{info.text}</p>
    </div>
  </div>
{/if}

{#if step === 'choose'}
  <div class="a-card pick">
    <span class="a-card__rail" aria-hidden="true"></span>
    <div class="pick__body">
      <strong>Choose a statement</strong>
      <span class="meta">A CSV downloaded from the bank or the card. PDF is not read yet.</span>
      <label class="a-cta file">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v10m0-10-4 4m4-4 4 4M5 19h14" /></svg>
        Choose a file
        <input type="file" accept={ACCEPT} onchange={chooseFile} />
      </label>
      {#if parseError}<p class="flag">{parseError}</p>{/if}
    </div>
  </div>
{:else}
  <div class="filebar">
    <div>
      <strong>{label}</strong>
      <span class="meta">{fileSummary}</span>
    </div>
    <button class="btn btn--sm" type="button" onclick={reset}>Start again</button>
  </div>
{/if}

{#if parseError && step !== 'choose'}
  <p class="flag">{parseError}</p>
{/if}

<!-- Stage one: the columns, shown only when the parser is not sure of them. -->
{#if step === 'mapping' && mapping && guess}
  <div class="stage">
    <h2 class="stage__title">Check the columns</h2>
    <p class="stage__why">
      This is what was worked out from the file. Correct anything that is wrong before the lines are
      read — a wrong date column moves a whole month of spending.
    </p>
    {#each guess.warnings as warning (warning)}
      <p class="flag">{warning}</p>
    {/each}
    <ul class="reasons">
      {#each reasons as note (note)}<li>{note}</li>{/each}
    </ul>

    <div class="picks">
      <label class="field">
        <span class="label">Date</span>
        <select value={String(mapping.dateIndex)} onchange={(e) => setColumn('dateIndex', e.currentTarget.value)}>
          {#each columnNumbers as index (index)}<option value={String(index)}>{columnLabel(index)}</option>{/each}
        </select>
      </label>
      <label class="field">
        <span class="label">Dates read as</span>
        <select value={mapping.dateOrder} onchange={(e) => setOrder(e.currentTarget.value)}>
          <option value="ymd">Year first — 2026-08-03</option>
          <option value="dmy">Day first — 03/08/2026</option>
          <option value="mdy">Month first — 08/03/2026</option>
        </select>
      </label>
      <label class="field">
        <span class="label">Description</span>
        <select
          value={String(mapping.descriptionIndex)}
          onchange={(e) => setColumn('descriptionIndex', e.currentTarget.value)}
        >
          {#each columnNumbers as index (index)}<option value={String(index)}>{columnLabel(index)}</option>{/each}
        </select>
      </label>
      <label class="field">
        <span class="label">Money</span>
        <select value={mapping.shape} onchange={(e) => setShape(e.currentTarget.value)}>
          <option value="single">One amount column</option>
          <option value="debit_credit">Separate money out and money in</option>
        </select>
      </label>
      {#if mapping.shape === 'single'}
        <label class="field">
          <span class="label">Amount</span>
          <select value={String(mapping.amountIndex)} onchange={(e) => setColumn('amountIndex', e.currentTarget.value)}>
            {#each columnNumbers as index (index)}<option value={String(index)}>{columnLabel(index)}</option>{/each}
          </select>
        </label>
        <label class="field">
          <span class="label">Money out is</span>
          <select
            value={mapping.debitIsPositive ? 'positive' : 'negative'}
            onchange={(e) => setSigns(e.currentTarget.value)}
          >
            <option value="positive">A positive amount</option>
            <option value="negative">A negative amount</option>
          </select>
        </label>
      {:else}
        <label class="field">
          <span class="label">Money out</span>
          <select value={String(mapping.debitIndex)} onchange={(e) => setColumn('debitIndex', e.currentTarget.value)}>
            {#each columnNumbers as index (index)}<option value={String(index)}>{columnLabel(index)}</option>{/each}
          </select>
        </label>
        <label class="field">
          <span class="label">Money in</span>
          <select value={String(mapping.creditIndex)} onchange={(e) => setColumn('creditIndex', e.currentTarget.value)}>
            {#each columnNumbers as index (index)}<option value={String(index)}>{columnLabel(index)}</option>{/each}
          </select>
        </label>
      {/if}
    </div>

    <div class="scroller">
      <table class="peek">
        <tbody>
          {#each guess.dataRows.slice(0, 4) as row, i (i)}
            <tr>
              {#each columnNumbers as index (index)}<td>{row[index] ?? ''}</td>{/each}
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <button class="a-cta" type="button" onclick={applyMapping}>Read the lines</button>
  </div>
{/if}

<!-- Stage two: what could not be read. Shown before the table, never after. -->
{#if step === 'skipped' && built}
  <div class="stage">
    <h2 class="stage__title">{built.skipped.length} row{built.skipped.length === 1 ? '' : 's'} could not be read</h2>
    <p class="stage__why">
      These are left out of the import. Usually they are the bank's own header block or a balance
      line — but look, in case a real transaction is among them.
    </p>
    <div class="scroller">
      <table class="peek">
        <tbody>
          {#each built.skipped as row (row.row)}
            <tr>
              <td class="a-num">{row.row}</td>
              <td>{row.cells.join(' · ')}</td>
              <td class="why">{row.reason}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
    <div class="stage__btns">
      <button class="a-cta" type="button" onclick={() => void openReview()}>
        Carry on with {built.lines.length} line{built.lines.length === 1 ? '' : 's'}
      </button>
      <button class="btn" type="button" onclick={() => (step = 'mapping')}>Change the columns</button>
    </div>
  </div>
{/if}

<!-- Stage three: the table. -->
{#if step === 'review'}
  <div class="stage">
    <h2 class="stage__title">What to import</h2>

    <div class="balances">
      <label class="field">
        <span class="label">Opening balance</span>
        <input
          type="text"
          inputmode="decimal"
          bind:value={openingBalance}
          placeholder="0.00"
          aria-invalid={fieldErrors.opening_balance ? 'true' : undefined}
        />
        {#if fieldErrors.opening_balance}<span class="field__flag">{fieldErrors.opening_balance}</span>{/if}
      </label>
      <label class="field">
        <span class="label">Closing balance</span>
        <input
          type="text"
          inputmode="decimal"
          bind:value={closingBalance}
          placeholder="0.00"
          aria-invalid={fieldErrors.closing_balance ? 'true' : undefined}
        />
        {#if fieldErrors.closing_balance}<span class="field__flag">{fieldErrors.closing_balance}</span>{/if}
      </label>
      <p class="balances__hint">From the statement. Leave blank to skip the check.</p>
    </div>

    {#if checking}
      <p class="meta"><span class="spinner" aria-hidden="true"></span> Checking these against what is already saved…</p>
    {/if}
    {#if alreadyImported}
      <p class="flag">
        This same file was imported on {alreadyImported.imported_at.slice(0, 10)} as
        “{alreadyImported.label}”. Importing it again will save its lines a second time.
      </p>
    {/if}
    {#if duplicateCount > 0}
      <p class="flag flag--warn">
        {duplicateCount} line{duplicateCount === 1 ? ' is' : 's are'} already on the books and
        start{duplicateCount === 1 ? 's' : ''} un-ticked. Tick one only if it really happened twice.
      </p>
    {/if}
    {#if leaseCount > 0}
      <p class="flag flag--warn">
        {leaseCount} line{leaseCount === 1 ? ' looks' : 's look'} like a standing cost already tracked
        on the Standing monthly costs tab. Importing it here only adds it to Total Breakdown — it has
        no effect on the Performance Dashboard.
      </p>
    {/if}
    {#if creditCount > 0}
      <p class="meta">
        {creditCount} line{creditCount === 1 ? ' is' : 's are'} money in rather than spending, so
        {creditCount === 1 ? 'it starts' : 'they start'} un-ticked.
      </p>
    {/if}

    <div class="bulk">
      <button class="btn btn--sm" type="button" onclick={() => setAll(true)}>Tick all</button>
      <button class="btn btn--sm" type="button" onclick={() => setAll(false)}>Untick all</button>
      <span class="bulk__count a-num">{chosen.length} of {rows.length} · {money(chosenTotal)}</span>
    </div>

    <!-- A month of fuel for one job should not take forty clicks. -->
    <div class="bulk bulk--jobsite">
      <label class="field field--inline">
        <span class="label">Set every line to…</span>
        <select bind:value={bulkJobSite}>
          <option value="">Company-wide</option>
          {#each activeSites as site (site.id)}
            <option value={String(site.id)}>{site.name}</option>
          {/each}
        </select>
      </label>
      <button class="btn btn--sm" type="button" onclick={() => setAllJobSites(bulkJobSite)}>
        Apply to every line
      </button>
    </div>

    <ul class="a-list">
      {#each rows as row, i (row.line.row)}
        {@const flagged = (row.check?.duplicates.length ?? 0) > 0 || (row.check?.income_duplicates.length ?? 0) > 0}
        {@const leased = (row.check?.lease_clashes.length ?? 0) > 0}
        <li style="--i: {i}">
          <div class="a-card line" class:line--off={!row.include}>
            <span class="a-card__rail" class:a-card__rail--muted={!row.include} aria-hidden="true"></span>
            <div class="line__body">
              <label class="check check--wide">
                <input type="checkbox" bind:checked={row.include} />
                <span class="line__head">
                  <span class="line__name">{row.line.description || 'No description'}</span>
                  <span class="line__sub">
                    <span class="a-num">{row.line.incurred_on}</span>
                    {#if row.line.direction === 'credit'} · money in{/if}
                  </span>
                </span>
                <span class="line__amount a-num">{money(row.line.amount)}</span>
              </label>

              {#if flagged}
                <p class="flagline">
                  Already saved:
                  {#each row.check?.duplicates ?? [] as dup (dup.expense_id)}
                    <span class="a-num">{dup.incurred_on}</span>
                    {dup.merchant || dup.description}
                    <span class="a-num">{money(dup.amount_total)}</span>
                    <span>{whereFrom(dup)}</span>
                  {/each}
                  {#each row.check?.income_duplicates ?? [] as dup (dup.income_id)}
                    <span class="a-num">{dup.incurred_on}</span>
                    {dup.source_label || dup.description}
                    <span class="a-num">{money(dup.amount_total)}</span>
                    <span>{whereFromIncome(dup)}</span>
                  {/each}
                </p>
              {/if}
              {#if leased}
                <p class="flagline flagline--lease">
                  Looks like the same payment as the standing cost
                  {#each row.check?.lease_clashes ?? [] as clash (clash.lease_id)}
                    “{clash.name}” at <span class="a-num">{money(clash.monthly_amount)}</span> a
                    month — worth checking that record still matches, on the Standing monthly
                    costs tab.
                  {/each}
                </p>
              {/if}

              {#if row.include}
                <div class="line__edit">
                  {#if row.line.direction === 'credit'}
                    <!-- Income has no category — a source label stands where the picker would be. -->
                    <div class="field">
                      <span class="label">Where it came from</span>
                      <p class="sourcelabel">{row.line.description || 'Not stated on the statement'}</p>
                    </div>
                  {:else}
                    <label class="field">
                      <span class="label">Category</span>
                      <select bind:value={row.category}>
                        <option value="">Uncategorised</option>
                        {#each liveCategories as category (category.id)}
                          <option value={String(category.id)}>{category.name}</option>
                        {/each}
                      </select>
                    </label>
                  {/if}
                  <label class="field">
                    <span class="label">What it was for</span>
                    <input type="text" bind:value={row.description} placeholder="optional" />
                  </label>
                  <label class="field">
                    <span class="label">Job site</span>
                    <select bind:value={row.jobSite}>
                      <option value="">Company-wide</option>
                      {#each activeSites as site (site.id)}
                        <option value={String(site.id)}>{site.name}</option>
                      {/each}
                    </select>
                  </label>
                  <label class="check">
                    <input type="checkbox" bind:checked={row.hadGst} />
                    Had GST
                  </label>
                </div>
              {/if}
            </div>
          </div>
        </li>
      {/each}
    </ul>

    <div class="commit">
      <p class="commit__sum">
        <span>Importing</span>
        <b class="a-num">{money(chosenTotal)}</b>
        <small class="a-num">ex-GST {money(chosenExGst)}</small>
      </p>
      <div class="stage__btns">
        <button class="a-cta" type="button" disabled={committing || chosen.length === 0} onclick={() => void commit()}>
          Import {chosen.length} line{chosen.length === 1 ? '' : 's'}
        </button>
        <button class="btn" type="button" onclick={() => (step = 'mapping')}>Change the columns</button>
      </div>
    </div>
  </div>
{/if}

<!-- What has been imported before. -->
<h2 class="stage__title past">Imports</h2>
{#if statements.length === 0}
  <div class="empty">
    <strong>Nothing imported yet</strong>
    <span class="meta">An import keeps its name, its period and its total — never the file.</span>
  </div>
{:else}
  <ul class="a-list">
    {#each statements as statement, i (statement.id)}
      <li style="--i: {i}">
        <div class="a-card past__card">
          <span class="a-card__rail" aria-hidden="true"></span>
          <div class="line__body">
            <div class="line__head">
              <div>
                <span class="line__name">{statement.label}</span>
                <span class="line__sub">
                  <span class="a-num">{statement.period_start ?? '?'} – {statement.period_end ?? '?'}</span>
                  · {statement.expense_count + statement.income_count} of {statement.line_count} still saved
                </span>
              </div>
              <b class="a-num">{money(statement.total_amount)}</b>
            </div>
            <div class="acts">
              <button class="btn btn--sm" type="button" onclick={() => void toggleStatement(statement.id)}>
                {openStatement === statement.id ? 'Hide' : 'What it brought in'}
              </button>
              {#if confirmDelete !== statement.id}
                <button class="btn btn--sm btn--ghost" type="button" onclick={() => (confirmDelete = statement.id)}>
                  Remove record
                </button>
              {/if}
            </div>
            {#if confirmDelete === statement.id}
              <div class="ask">
                <p>Remove the record of {statement.label}? {keptPhrase(statement)}</p>
                <div class="ask__btns">
                  <button class="btn btn--sm btn--danger" type="button" onclick={() => void removeStatement(statement.id)}>
                    Yes, remove it
                  </button>
                  <button class="btn btn--sm" type="button" onclick={() => (confirmDelete = null)}>No</button>
                </div>
              </div>
            {/if}
            {#if openStatement === statement.id}
              <ul class="brought">
                {#each openLines as line, li (li)}
                  <li>
                    <span class="a-num">{line.date}</span>
                    <!-- A leading space inside an {#if} is collapsed away (see `fileSummary`
                         above), so the tag is built as one string instead. -->
                    <span class="brought__name">{line.label}{line.direction === 'credit' ? ' · money in' : ''}</span>
                    <span class="a-num">{money(line.amount)}</span>
                  </li>
                {:else}
                  <li class="meta">Nothing from this import is still saved.</li>
                {/each}
              </ul>
            {/if}
          </div>
        </div>
      </li>
    {/each}
  </ul>
{/if}

<style>
  .note {
    margin: 0 0 0.7rem;
    font-size: 0.84rem;
    color: var(--ink-soft);
  }

  .pick {
    grid-template-columns: auto 1fr;
    align-items: stretch;
  }
  .pick:hover {
    transform: none;
    box-shadow: var(--shadow);
  }
  .pick__body {
    display: grid;
    gap: 0.5rem;
    justify-items: start;
    min-width: 0;
    padding: 0.2rem 0;
  }
  /* The input itself is never shown: the label IS the button, so the tap
     target is the pill rather than a browser-drawn control. */
  .file input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }
  .file {
    position: relative;
  }
  .file:focus-within {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
  }

  .filebar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.6rem 0.75rem;
    margin-bottom: 0.8rem;
    background: var(--paper);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
  }
  .filebar strong {
    display: block;
    overflow-wrap: anywhere;
  }

  .stage {
    display: grid;
    gap: 0.6rem;
    padding: 0.85rem 0.8rem 0.9rem;
    margin-bottom: 0.9rem;
    background: var(--paper);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
  }
  .stage__title {
    margin: 0;
    font-size: 1.05rem;
    letter-spacing: -0.01em;
  }
  .stage__why {
    margin: 0;
    font-size: 0.84rem;
    color: var(--ink-soft);
  }
  .stage__btns {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .past {
    margin: 1.2rem 0 0.6rem;
  }

  .flag {
    margin: 0;
    padding: 0.5rem 0.65rem;
    border-radius: var(--radius);
    border-left: 4px solid var(--hold);
    background: var(--hold-tint);
    font-size: 0.85rem;
    font-weight: 600;
  }
  /* Amber, not red: a line that needs a look is not a fault. */
  .flag--warn {
    border-left-color: var(--warn);
    background: var(--warn-tint);
  }
  .flagline {
    margin: 0;
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--hold);
  }
  .flagline--lease {
    color: var(--warn);
  }

  .reasons {
    margin: 0;
    padding-left: 1.1rem;
    font-size: 0.8rem;
    color: var(--ink-faint);
  }

  .picks {
    display: grid;
    gap: 0.5rem;
    grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  }

  /* The reconciliation inputs, styled like .picks — a grid of fields, not a
     table row — because there are only ever two of them. */
  .balances {
    display: grid;
    gap: 0.4rem 0.6rem;
    grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
    align-items: end;
  }
  .balances .field {
    margin-bottom: 0;
  }
  .balances__hint {
    grid-column: 1 / -1;
    margin: 0;
    font-size: 0.78rem;
    color: var(--ink-faint);
  }

  /* A wide table scrolls inside its own box; the page never scrolls sideways. */
  .scroller {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border-radius: var(--radius);
    background: var(--bg-sunken);
  }
  .peek {
    border-collapse: collapse;
    font-size: 0.76rem;
    font-variant-numeric: tabular-nums;
  }
  .peek td {
    padding: 0.3rem 0.5rem;
    white-space: nowrap;
    border-bottom: 1px solid var(--line);
    color: var(--ink-soft);
  }
  .peek .why {
    color: var(--ink-faint);
    font-style: italic;
  }

  .bulk {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem 0.6rem;
  }
  .bulk__count {
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--ink-faint);
  }
  .bulk--jobsite {
    padding: 0.4rem 0.55rem;
    border-radius: var(--radius);
    background: var(--bg-sunken);
  }
  .bulk--jobsite .field--inline {
    margin-bottom: 0;
    min-width: 10rem;
  }

  .line {
    grid-template-columns: auto 1fr;
    align-items: stretch;
  }
  .line:hover {
    transform: none;
    box-shadow: var(--shadow);
  }
  .line--off {
    background: var(--bg-sunken);
  }
  .line__body {
    display: grid;
    gap: 0.45rem;
    min-width: 0;
    padding: 0.1rem 0;
  }
  .line__head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.2rem 0.8rem;
    min-width: 0;
  }
  .line__name {
    display: block;
    font-weight: 700;
    overflow-wrap: anywhere;
  }
  .line__sub {
    display: block;
    font-size: 0.76rem;
    font-weight: 400;
    color: var(--ink-faint);
  }
  .line__amount {
    margin-left: auto;
    font-weight: 700;
  }
  .line__edit {
    display: grid;
    gap: 0.45rem;
    grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
    align-items: end;
  }
  /* Stands where the category picker would be on a credit line — same
     field/label shape, so the row doesn't jump as it's read. */
  .sourcelabel {
    margin: 0;
    min-height: calc(var(--tap) - 4px);
    display: flex;
    align-items: center;
    font-size: 0.9rem;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
  .check {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    min-height: var(--tap);
    padding: 0 0.85rem 0 0.65rem;
    border: 1.5px solid var(--line);
    border-radius: 999px;
    background: var(--paper);
    font-size: 0.9rem;
    font-weight: 700;
    color: var(--ink-soft);
    cursor: pointer;
    touch-action: manipulation;
  }
  .check input {
    width: 1.15rem;
    height: 1.15rem;
    margin: 0;
    flex: 0 0 auto;
    accent-color: var(--brand);
  }
  .check:has(input:checked) {
    border-color: var(--brand);
  }
  .check:focus-within {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
  }
  .check--wide {
    width: 100%;
    border-radius: var(--radius);
    padding: 0.35rem 0.6rem;
    color: var(--ink);
    text-align: left;
  }

  .commit {
    display: grid;
    gap: 0.5rem;
  }
  .commit__sum {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0.2rem 0.5rem;
    margin: 0;
    padding: 0.4rem 0.6rem;
    border-radius: var(--radius);
    background: var(--bg-sunken);
  }
  .commit__sum span {
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
  }
  .commit__sum b {
    font-size: 1.05rem;
  }
  .commit__sum small {
    font-size: 0.78rem;
    color: var(--ink-faint);
  }

  .past__card {
    grid-template-columns: auto 1fr;
    align-items: stretch;
  }
  .past__card:hover {
    transform: none;
    box-shadow: var(--shadow);
  }
  .acts {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }
  .brought {
    list-style: none;
    margin: 0;
    padding: 0.4rem 0.55rem;
    display: grid;
    gap: 0.3rem;
    border-radius: var(--radius);
    background: var(--bg-sunken);
    font-size: 0.8rem;
  }
  .brought li {
    display: flex;
    gap: 0.5rem;
    align-items: baseline;
  }
  .brought__name {
    flex: 1 1 auto;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  /* Inline banner: this app has no modals. */
  .ask {
    display: grid;
    gap: 0.5rem;
    padding: 0.6rem 0.7rem;
    border-radius: var(--radius);
    border-left: 4px solid var(--hold);
    background: var(--hold-tint);
  }
  .ask p {
    margin: 0;
    font-size: 0.88rem;
    font-weight: 600;
  }
  .ask__btns {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  .empty {
    display: grid;
    gap: 0.4rem;
    justify-items: start;
    background: var(--paper);
    border-radius: var(--radius-lg);
    padding: 1.2rem;
    box-shadow: var(--shadow);
  }
</style>
